import { FC } from 'react';
import { Badge } from '@crisellevaldez/speculo-ui';
import { TranscriptConversation } from '../../common/TranscriptConversation';
import { CallSummaryText } from './CallSummaryText';
import { CallRecordingPlayer } from '../../common/CallRecordingPlayer';
import { CrmStageDisplay } from '../../common/CrmStageDisplay';
import type { CrmStageProjection } from '../../../types/crmStage.types';
import {
  AnsweredBy,
  getAnsweredByStyle,
  getCallOutcomeStyle,
  getFlowTypeStyle,
  formatOutcome,
} from '../../../utils/badgeStyles';
import { formatDate, type DateInput } from '../../../utils/dateTime';
import { formatDurationFromSeconds } from '../../../utils/duration';
import { formatPhoneNumber } from '../../../utils/formatters';
import {
  User,
  Voicemail,
  XCircle,
  CheckCircle,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Calendar,
  Clock,
  GitBranch,
  Pencil,
  MessageSquare,
  Target,
  TrendingUp,
  UserCheck,
} from 'lucide-react';
import { Contact } from '../../../types/contact.types';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { resolveAssignedAgent, formatAssignedAgentName } from '../../../utils/assignedAgent';
import type { GQLTransferSummary } from '../../../hooks/graphql/dashboard/useOrgLiveTransfersGQL';
import { formatTransferReason } from '../../../utils/transferReason';
import { stripTemplateVariables } from '../../../utils/templateVars';

interface Call {
  id: string;
  call_id: string;
  answered_by?: AnsweredBy;
  callTimestamp?: DateInput;
  call_analysis?: {
    outcome: string;
    notes?: string;
    timeline?: Array<{ event: string; timestamp: string }>;
    answers?: {
      'Willingness to Speak with an Agent'?: string;
      'Transaction Type'?: string;
      Motivation?: string;
      [key: string]: string | undefined;
    };
    summary?: string;
  };
  duration?: number;
  corrected_duration?: number;
  concatenated_transcript?: string;
  inbound?: boolean;
  recordingUrl?: string;
  isTest?: boolean | null;
  /** Live transfer that this call initiated, when present. */
  transfer?: GQLTransferSummary | null;
}

interface CallDetailsPanelProps {
  call: Call;
  orgTimezone?: string;
  flowInfo?: {
    name: string;
    type: string;
  };
  contact?: Contact;
  contactFirstName?: string;
  assistantName?: string;
  displayName?: string;
  displayPhone?: string;
  onEditContact?: () => void;
  onEditOutcome?: () => void;
  /** The lead's CRM stage, shown under their contact details; omit to hide it. */
  crmStages?: readonly CrmStageProjection[] | null;
}

export const CallDetailsPanel: FC<CallDetailsPanelProps> = ({
  call,
  orgTimezone,
  flowInfo,
  contact,
  contactFirstName,
  assistantName,
  displayName,
  displayPhone,
  onEditContact,
  onEditOutcome,
  crmStages,
}) => {
  const { users: orgUsers } = useOrganizationUsers();

  if (!call) return null;

  const answeredBy = call.answered_by || 'no answer';
  const answeredByStyle = getAnsweredByStyle(answeredBy);
  const outcome = call.call_analysis?.outcome;
  const outcomeStyle = outcome ? getCallOutcomeStyle(outcome) : null;
  const answers = call.call_analysis?.answers;
  const willingness = answers?.['Willingness to Speak with an Agent'];
  const transactionType = answers?.['Transaction Type'];
  const motivation = answers?.Motivation;
  const timeline = call.call_analysis?.timeline;
  const hasAnalysis = willingness || transactionType || motivation;

  const contactFullName = contact
    ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
    : '';
  const shownName = contactFullName || displayName || '';
  const shownPhone = contact?.phoneNumber || displayPhone || '';

  return (
    <div className="h-full flex flex-col overflow-y-auto scrollbar-custom">
      <div className="flex flex-col">

        {/* Contact Info */}
        {(contact || shownName) && (
          <div className="px-4 sm:px-5 py-4 sm:py-5 border-b border-border">
            <div className="flex items-start justify-between gap-2 sm:gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 min-w-0">
                  <p className="t-name truncate">{shownName || '-'}</p>
                  {shownPhone && (
                    <span className="t-label shrink-0 whitespace-nowrap">
                      {formatPhoneNumber(shownPhone)}
                    </span>
                  )}
                </div>
                {contact?.email && (
                  <p className="t-label truncate mt-0.5">{contact.email}</p>
                )}
                {crmStages !== undefined && (
                  <div className="flex items-center gap-1.5 mt-1 min-w-0">
                    <span className="t-label shrink-0 text-foreground/80">CRM stage:</span>
                    <CrmStageDisplay stages={crmStages} compact />
                  </div>
                )}
              </div>
              {onEditContact && contact && (
                <button
                  onClick={onEditContact}
                  className="t-body text-primary hover:text-primary/80 font-medium shrink-0 flex items-center gap-1"
                >
                  <Pencil className="t-icon-sm" />
                  <span className="hidden sm:inline">Edit</span>
                </button>
              )}
            </div>
            {(() => {
              const agent = resolveAssignedAgent(
                contact?.assignedAgent,
                contact?.assignedAgentId,
                orgUsers
              );
              const name = formatAssignedAgentName(agent);
              if (!name) return null;
              return (
                <div className="mt-3 pt-3 border-t border-border flex items-center gap-2">
                  <UserCheck className="t-icon text-success" />
                  <span className="t-label">
                    Assigned to <span className="t-value">{name}</span>
                  </span>
                </div>
              );
            })()}
          </div>
        )}

        {/* Call Details */}
        <div className="px-4 sm:px-5 py-4 sm:py-5 border-b border-border space-y-3.5">
          <span className="t-heading">Call Details</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div className="flex items-center gap-2 min-w-0">
              {call.inbound ? (
                <PhoneIncoming className="t-icon call-color-inbound shrink-0" />
              ) : (
                <PhoneOutgoing className="t-icon call-color-outbound shrink-0" />
              )}
              <span className="t-value truncate">{call.inbound ? 'Inbound' : 'Outbound'}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <Clock className="t-icon shrink-0" />
              <span className="t-value">{(call.corrected_duration || call.duration) ? formatDurationFromSeconds(call.corrected_duration ?? call.duration) : '-'}</span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <Calendar className="t-icon shrink-0" />
              <span className="t-body truncate">{call.callTimestamp ? formatDate(call.callTimestamp, orgTimezone, 'MMM D, YYYY h:mm A z') : '-'}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              {call.isTest && (
                <Badge className="inline-flex items-center gap-1 badge-amber">
                  Test
                </Badge>
              )}
              {answeredByStyle && (
                <Badge
                  className={`inline-flex items-center gap-1 ${answeredByStyle.background} ${answeredByStyle.text}`}
                >
                  {answeredBy === 'human' ? (
                    <><User className="t-icon-sm" /><span>Human</span></>
                  ) : answeredBy === 'voicemail' ? (
                    <><Voicemail className="t-icon-sm" /><span>Voicemail</span></>
                  ) : (
                    <><XCircle className="t-icon-sm" /><span>No Answer</span></>
                  )}
                </Badge>
              )}
              {outcome && outcomeStyle && (
                <button onClick={onEditOutcome} className="inline-flex items-center">
                  <Badge
                    className={`inline-flex items-center gap-1 cursor-pointer ${outcomeStyle.background} ${outcomeStyle.text}`}
                  >
                    <span>{formatOutcome(outcome)}</span>
                    <Pencil className="w-2.5 h-2.5" />
                  </Badge>
                </button>
              )}
              {!outcome && onEditOutcome && (
                <button
                  onClick={onEditOutcome}
                  className="inline-flex items-center gap-1 t-label hover:text-foreground"
                >
                  <span>No outcome</span>
                  <Pencil className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
          </div>
          {call.recordingUrl && (
            <CallRecordingPlayer callId={call.call_id} hasRecording={!!call.recordingUrl} />
          )}
        </div>

        {/* Live Transfer — shown when this call initiated a live transfer.
            Mirrors the Transfer Details block in LiveTransfersModal. */}
        {call.transfer && (
          <div className="px-4 sm:px-5 py-4 sm:py-5 border-b border-border space-y-3.5">
            <span className="t-heading">Live Transfer</span>
            {call.transfer.answered === true && (
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle className="t-icon-sm shrink-0 text-green-600 dark:text-green-400" />
                <span className="t-label shrink-0">Answered by:</span>
                <span className="t-body truncate">
                  {call.transfer.acceptedBy
                    ? call.transfer.acceptedBy
                    : call.transfer.targetAgentNumber
                      ? formatPhoneNumber(call.transfer.targetAgentNumber)
                      : '—'}
                </span>
              </div>
            )}
            {call.transfer.answered === false && (
              <div className="flex items-center gap-2 min-w-0">
                <XCircle className="t-icon-sm shrink-0 text-muted-foreground" />
                <span className="t-label shrink-0">Answered:</span>
                <span className="t-body">No answer</span>
              </div>
            )}
            {call.transfer.failureReason && (
              <div className="flex items-start gap-2 min-w-0">
                <XCircle className="t-icon-sm shrink-0 text-red-500 mt-0.5" />
                <span className="t-label shrink-0">Failure:</span>
                <span className="t-body">{formatTransferReason(call.transfer.failureReason)}</span>
              </div>
            )}
            {(call.transfer.recordingUrl || call.transfer.voicemailUrl) &&
              call.transfer.transferId && (
                <div className="flex flex-col gap-3">
                  {call.transfer.recordingUrl && (
                    <div className="flex items-center gap-2 min-w-0">
                      <Phone className="t-icon-sm shrink-0 text-primary" />
                      <span className="t-label shrink-0">Transfer:</span>
                      <CallRecordingPlayer
                        transferId={call.transfer.transferId}
                        kind="recording"
                        hasRecording={!!call.transfer.recordingUrl}
                      />
                    </div>
                  )}
                  {call.transfer.voicemailUrl && (
                    <div className="flex items-center gap-2 min-w-0">
                      <Voicemail className="t-icon-sm shrink-0 text-primary" />
                      <span className="t-label shrink-0">Voicemail:</span>
                      <CallRecordingPlayer
                        transferId={call.transfer.transferId}
                        kind="voicemail"
                        hasRecording={!!call.transfer.voicemailUrl}
                      />
                    </div>
                  )}
                </div>
              )}
            {stripTemplateVariables(call.transfer.transferReason) && (
              <div className="space-y-2">
                <span className="t-label">Reason for transfer</span>
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-[#A8F3D0] dark:border-green-700/30">
                  <p className="text-gray-700 dark:text-gray-300 text-sm whitespace-pre-wrap">
                    {stripTemplateVariables(call.transfer.transferReason)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Flow & Analysis */}
        {(flowInfo || hasAnalysis || (timeline && timeline.length > 0)) && (
          <div className="px-4 sm:px-5 py-4 sm:py-5 border-b border-border space-y-3.5">
            <span className="t-heading">Analysis</span>

            {flowInfo && (
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <GitBranch className="t-icon shrink-0" />
                <span className="t-label shrink-0">Flow:</span>
                <span className="t-value truncate min-w-0">{flowInfo.name}</span>
                {flowInfo.type && flowInfo.type !== '-' && (
                  <Badge

                    className={`t-caption shrink-0 ${getFlowTypeStyle(flowInfo.type).background} ${getFlowTypeStyle(flowInfo.type).text}`}
                  >
                    {flowInfo.type}
                  </Badge>
                )}
              </div>
            )}

            {willingness && (
              <div className="flex items-start gap-2">
                <MessageSquare className="t-icon mt-0.5 shrink-0" />
                <div className="t-body">
                  <span className="t-label">Willing to speak:</span>{' '}
                  <span className={willingness.toLowerCase() === 'yes' ? 'text-emerald-600 font-semibold' : 'text-red-500 font-semibold'}>
                    {willingness}
                  </span>
                </div>
              </div>
            )}

            {transactionType && (
              <div className="flex items-start gap-2">
                <TrendingUp className="t-icon mt-0.5 shrink-0" />
                <div className="t-body">
                  <span className="t-label">Transaction:</span>{' '}
                  <span className="t-value">{transactionType}</span>
                </div>
              </div>
            )}

            {motivation && (
              <div className="flex items-start gap-2">
                <Target className="t-icon mt-0.5 shrink-0" />
                <div className="t-body">
                  <span className="t-label">Motivation:</span>{' '}
                  <span className="t-body">{motivation}</span>
                </div>
              </div>
            )}

            {timeline && timeline.length > 0 && timeline.some((t) => t.event && t.timestamp) && (
              <div className="pt-1 space-y-1.5">
                <span className="t-caption font-semibold uppercase tracking-wide">Timeline</span>
                {timeline
                  .filter((t) => t.event && t.timestamp)
                  .map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="t-label tabular-nums w-16 shrink-0">{item.timestamp}</span>
                      <span className="t-body">{item.event}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Call Summary */}
        <div className="px-4 sm:px-5 py-4 sm:py-5 border-b border-border space-y-2.5">
          <span className="t-heading">Summary</span>
          {call.call_analysis?.summary ? (
            <CallSummaryText summary={call.call_analysis.summary} />
          ) : (
            <p className="t-empty">No summary available</p>
          )}
        </div>

        {/* Transcript */}
        <div className="px-4 sm:px-5 py-4 sm:py-5 space-y-2.5">
          <span className="t-heading">Transcript</span>
          {call.concatenated_transcript ? (
            <TranscriptConversation
              transcript={call.concatenated_transcript}
              contactFirstName={contactFirstName}
              assistantName={assistantName}
            />
          ) : (
            <p className="t-empty">No transcript available</p>
          )}
        </div>

      </div>
    </div>
  );
};
